import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, EyeOff, RotateCcw, TrendingUp, ArrowLeft, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";

const OUTCOME_COLORS = {
  WIN_TP:   "text-emerald-700 bg-emerald-100",
  WIN_MOON: "text-emerald-800 bg-emerald-200",
  WIN_TIME: "text-emerald-600 bg-emerald-50",
  LOSS_SL:  "text-red-700 bg-red-100",
  LOSS_TIME:"text-red-600 bg-red-50",
  OPEN:     "text-neutral-500 bg-neutral-100",
};

export default function AdminBacktest() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // alert_id currently mutating
  const [reason, setReason] = useState("");
  const [days, setDays] = useState(180);
  const [showExcluded, setShowExcluded] = useState(true);
  // Loss cap slider — "what if we manually stopped every loser at -N%?"
  // Purely visual tuning tool for the analysts; does NOT mutate stored data.
  // 0 = off (use the trades' actual returns), otherwise floor negatives at -cap.
  const [lossCapPct, setLossCapPct] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/backtest/alerts?days=${days}`);
      setRows(data.rows);
    } catch (e) {
      toast.error("Failed to load backtest data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (row) => {
    setBusy(row.id);
    try {
      await api.post("/admin/backtest/toggle-exclusion", {
        alert_id: row.id,
        excluded: !row.excluded_from_stats,
        reason: !row.excluded_from_stats ? reason : null,
      });
      setReason("");
      await load();
      toast.success(row.excluded_from_stats ? "Re-included in stats." : "Excluded from stats.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setBusy(null);
    }
  };

  // Local live stats — reflect the flag toggles instantly, no server roundtrip
  const included = rows.filter((r) => !r.excluded_from_stats);
  const closed = included.filter((r) => r.outcome && r.outcome !== "OPEN");
  // Apply the analyst's "what-if" loss cap when > 0 — floor every negative return
  // at -lossCapPct so the desk can see the stat impact of a tighter manual stop
  // without touching the underlying alert data.
  const capReturn = (r) => {
    const raw = r.return_pct;
    if (raw == null) return null;
    if (lossCapPct > 0 && raw < -lossCapPct) return -lossCapPct;
    return raw;
  };
  const closedWithCap = closed.map((r) => ({ ...r, _capped: capReturn(r) }));
  const wins = closedWithCap.filter((r) => (r._capped ?? 0) > 0);
  const winRate = closedWithCap.length ? (wins.length / closedWithCap.length * 100).toFixed(1) : "0.0";
  const avgReturn = closedWithCap.length
    ? (closedWithCap.reduce((s, r) => s + (r._capped || 0), 0) / closedWithCap.length).toFixed(2)
    : "0.00";
  // Baseline (no cap) — show delta so the analyst can quantify the tightening
  const baseAvg = closed.length
    ? closed.reduce((s, r) => s + (r.return_pct || 0), 0) / closed.length
    : 0;
  const baseWins = closed.filter((r) => (r.return_pct || 0) > 0).length;
  const baseWinRate = closed.length ? (baseWins / closed.length * 100) : 0;
  const wrDelta = lossCapPct > 0 ? (parseFloat(winRate) - baseWinRate) : 0;
  const avgDelta = lossCapPct > 0 ? (parseFloat(avgReturn) - baseAvg) : 0;

  const visibleRows = showExcluded ? rows : rows.filter(r => !r.excluded_from_stats);

  return (
    <div className="min-h-screen bg-mb-cream text-neutral-900 pb-16">
      <header className="border-b border-mb-border bg-white sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-sm text-neutral-500 hover:text-mb-emerald flex items-center gap-1" data-testid="backtest-back-link">
              <ArrowLeft className="w-4 h-4" /> Admin
            </Link>
            <h1 className="font-display text-xl font-semibold">Backtest Editor</h1>
          </div>
          <div className="flex items-center gap-3 text-xs text-neutral-500">
            <span>Window:</span>
            <select
              className="border border-mb-border rounded px-2 py-1 bg-white"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="backtest-window-select"
            >
              <option value={30}>30d</option>
              <option value={90}>90d</option>
              <option value={180}>180d</option>
              <option value={365}>1y</option>
            </select>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={showExcluded} onChange={(e) => setShowExcluded(e.target.checked)} />
              Show excluded
            </label>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 pt-6">
        {/* Live stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="backtest-live-stats">
          <StatTile label="Included alerts" value={included.length} />
          <StatTile label="Closed" value={closed.length} />
          <StatTile
            label={lossCapPct > 0 ? `Win rate (cap −${lossCapPct}%)` : "Win rate"}
            value={`${winRate}%`}
            accent="emerald"
            delta={lossCapPct > 0 ? wrDelta : null}
            deltaSuffix="pp"
          />
          <StatTile
            label={lossCapPct > 0 ? `Avg return (cap −${lossCapPct}%)` : "Avg return"}
            value={`${avgReturn}%`}
            accent={parseFloat(avgReturn) >= 0 ? "emerald" : "red"}
            delta={lossCapPct > 0 ? avgDelta : null}
            deltaSuffix="pp"
          />
        </div>

        {/* Loss cap what-if slider — desk tuning tool, purely visual */}
        <div className="mb-4 bg-white border border-mb-border rounded-md px-4 py-3" data-testid="backtest-loss-cap-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-600" />
              <div>
                <div className="text-sm font-semibold text-neutral-900">Max loss cap (what-if)</div>
                <div className="text-[11px] text-neutral-500">
                  Floors every negative return at −N% so you can see the stat impact of tighter manual stops. Doesn&apos;t touch stored data.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono-data text-lg font-bold text-neutral-900 tabular-nums w-16 text-right" data-testid="backtest-loss-cap-value">
                {lossCapPct === 0 ? "Off" : `−${lossCapPct}%`}
              </span>
              <button
                type="button"
                className="text-[11px] text-mb-emerald hover:underline"
                onClick={() => setLossCapPct(0)}
                data-testid="backtest-loss-cap-reset"
              >
                Reset
              </button>
            </div>
          </div>
          <input
            type="range"
            min={0}
            max={15}
            step={0.5}
            value={lossCapPct}
            onChange={(e) => setLossCapPct(parseFloat(e.target.value))}
            className="w-full mt-3 accent-mb-emerald"
            data-testid="backtest-loss-cap-slider"
            aria-label="Max loss cap percent"
          />
          <div className="flex justify-between text-[10px] font-mono-data text-neutral-400 mt-1">
            <span>Off</span><span>−3%</span><span>−6%</span><span>−9%</span><span>−12%</span><span>−15%</span>
          </div>
        </div>

        {/* Exclusion reason input */}
        <div className="mb-4 flex gap-2 items-center bg-white border border-mb-border rounded-md px-3 py-2">
          <span className="text-xs text-neutral-500 whitespace-nowrap">Exclusion reason (optional):</span>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. test data · bad tape · vetting skipped"
            className="h-8 text-xs border-0 focus-visible:ring-0"
            data-testid="backtest-exclusion-reason-input"
          />
        </div>

        {loading ? (
          <div className="text-center py-16 text-neutral-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading…
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-mb-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Symbol</th>
                  <th className="text-left px-3 py-2">Class</th>
                  <th className="text-right px-3 py-2">Entry</th>
                  <th className="text-right px-3 py-2">Exit</th>
                  <th className="text-center px-3 py-2">Days</th>
                  <th className="text-right px-3 py-2">Return</th>
                  <th className="text-center px-3 py-2">Outcome</th>
                  <th className="text-right px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r) => {
                  const isEx = r.excluded_from_stats;
                  const rowClass = isEx ? "opacity-50 line-through" : "";
                  const retClass = r.return_pct == null ? "text-neutral-400" :
                    r.return_pct >= 0 ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold";
                  return (
                    <tr key={r.id} className={`border-t border-mb-border ${rowClass}`} data-testid={`backtest-row-${r.id}`}>
                      <td className="px-3 py-2 font-mono-data text-xs">{r.date}</td>
                      <td className="px-3 py-2 font-mono-data font-semibold">{r.symbol}</td>
                      <td className="px-3 py-2 text-xs">
                        <span className={`px-2 py-0.5 rounded ${r.alert_class === "moonshot" ? "bg-pink-100 text-pink-800" : "bg-emerald-100 text-emerald-800"}`}>
                          {r.alert_class}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono-data text-right">{r.entry_price?.toFixed(2) || "—"}</td>
                      <td className="px-3 py-2 font-mono-data text-right">{r.exit_price?.toFixed(2) || "—"}</td>
                      <td className="px-3 py-2 text-center font-mono-data text-xs">{r.days_held ?? "—"}</td>
                      <td className={`px-3 py-2 font-mono-data text-right ${retClass}`}>
                        {r.return_pct == null ? "—" : `${r.return_pct >= 0 ? "+" : ""}${r.return_pct.toFixed(2)}%`}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {r.outcome && (
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${OUTCOME_COLORS[r.outcome] || "text-neutral-500 bg-neutral-100"}`}>
                            {r.outcome}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant={isEx ? "outline" : "ghost"}
                          disabled={busy === r.id}
                          onClick={() => toggle(r)}
                          className="h-7 text-xs"
                          data-testid={`backtest-toggle-${r.id}`}
                          title={isEx ? (r.exclusion_reason ? `Excluded: ${r.exclusion_reason}` : "Excluded") : "Included in stats"}
                        >
                          {busy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                           isEx ? <><RotateCcw className="w-3.5 h-3.5 mr-1" />Include</> :
                                  <><EyeOff className="w-3.5 h-3.5 mr-1" />Exclude</>}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {visibleRows.length === 0 && (
              <div className="text-center py-10 text-neutral-500">
                <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No approved alerts in this window.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value, accent, delta, deltaSuffix }) {
  const color = accent === "emerald" ? "text-emerald-700" : accent === "red" ? "text-red-700" : "text-neutral-900";
  const showDelta = delta !== null && delta !== undefined && !Number.isNaN(delta);
  const dPos = showDelta && delta > 0.01;
  const dNeg = showDelta && delta < -0.01;
  const dColor = dPos ? "text-emerald-700" : dNeg ? "text-red-700" : "text-neutral-400";
  return (
    <div className="bg-white border border-mb-border rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`text-2xl font-mono-data font-semibold ${color} mt-1`}>{value}</div>
      {showDelta && (
        <div className={`text-[10px] font-mono-data mt-1 ${dColor}`} data-testid={`stat-delta-${label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
          {dPos ? "▲" : dNeg ? "▼" : "·"} {delta >= 0 ? "+" : ""}{delta.toFixed(2)}{deltaSuffix || ""} vs no-cap
        </div>
      )}
    </div>
  );
}
